import { Controller } from "@hotwired/stimulus";
import * as Luxon from "luxon";

export default class extends Controller {
  static targets = [
    "dayButton",
    "loadingContainer",
    "locale",
    "nextButtonSlots",
    "potentialSlotsContainer",
    "timeButton",
  ];

  connect() {
    this.currentInstructorId = this.readInstructorId();
    this.currentLocationId = this.readLocationId();
    this.currentTransmission = this.readAutomaticSelected();
    this.initializeState();
    this.ensureInitialDaySlotsLoaded();
  }

  initializeState() {
    const noFutureSlots = this.hasNoFutureSlots();
    const hasVisibleSlots = this.hasVisibleSlotButtons();
    const hasActiveSelectableDay = this.hasActiveSelectableDay();

    if (noFutureSlots) {
      this.toggleDashboardNoSlots(true);
      this.displayDashboardWeekMessage(false);
      this.setArrowButtonState(
        document.getElementById("slots-picker-previous-week-button"),
        true
      );
      this.setArrowButtonState(
        document.getElementById("slots-picker-next-week-button"),
        true
      );
      this.disableScheduleButton();
    } else if (hasVisibleSlots) {
      this.toggleDashboardNoSlots(false);
      this.displayDashboardWeekMessage(false);
      this.disableScheduleButton();
    } else if (hasActiveSelectableDay) {
      this.toggleDashboardNoSlots(false);
      this.displayDashboardWeekMessage(false);
      this.disableScheduleButton();
    } else {
      this.toggleDashboardNoSlots(false);
      this.displayDashboardWeekMessage(true);
      this.disableScheduleButton();
    }

    this.updateDashboardNavigationButtons();
  }

  hasVisibleSlotButtons() {
    return document.querySelector("#potential_slots .SlotPicker-Time") !== null;
  }

  hasActiveSelectableDay() {
    const activeDayButton = document.querySelector(
      ".SlotPicker-Day.isActive:not(.Button--disabled)"
    );
    return activeDayButton?.dataset?.slotDate != null;
  }

  async ensureInitialDaySlotsLoaded() {
    if (this.hasNoFutureSlots() || this.hasVisibleSlotButtons()) {
      return;
    }

    const activeDayButton = document.querySelector(
      ".SlotPicker-Day.isActive:not(.Button--disabled)"
    );
    if (!activeDayButton?.dataset?.slotDate) {
      return;
    }

    await this.get_lesson_slots(null, activeDayButton.dataset.slotDate);
  }

  async showLoader() {
    this.toggleDashboardNoSlots(false);

    if (this.hasPotentialSlotsContainerTarget) {
      this.potentialSlotsContainerTarget.classList.add("hidden");
    }
    if (this.hasLoadingContainerTarget) {
      this.loadingContainerTarget.classList.remove("hidden");
    }
  }

  async hideLoader() {
    if (this.hasPotentialSlotsContainerTarget) {
      this.potentialSlotsContainerTarget.classList.remove("hidden");
    }
    if (this.hasLoadingContainerTarget) {
      this.loadingContainerTarget.classList.add("hidden");
    }
  }

  toggleActiveDay(event) {
    event.preventDefault();

    if (event.currentTarget.classList.contains("Button--disabled")) {
      return;
    }

    this.dayButtonTargets.forEach((button) => {
      button.classList.remove("isActive");
    });

    event.currentTarget.classList.add("isActive");

    document.getElementById("picked_slot_day").value =
      event.currentTarget.textContent.trim();
    this.resetPickedSlotFields();
  }

  toggleActiveTime(event) {
    event.preventDefault();

    this.timeButtonTargets.forEach((button) => {
      button.classList.remove("isActive");
    });

    event.currentTarget.classList.add("isActive");

    document.getElementById("picked_slot_time").value =
      event.currentTarget.getAttribute("data-slot-time") || "";
    document.getElementById("picked_slot_iso_time").value =
      event.currentTarget.getAttribute("data-slot-iso-time");
    document.getElementById("picked_slot_id").value =
      event.currentTarget.getAttribute("data-slot-id");
    document.getElementById("picked_slot_is_master_lesson").value =
      event.currentTarget.getAttribute("data-is-master-lesson") || "false";
    document.getElementById("picked_slot_master_lesson_id").value =
      event.currentTarget.getAttribute("data-master-lesson-id") || "";
    this.enableScheduleButton();
  }

  async get_lesson_slots(event, startDate = null) {
    await this.showLoader();

    let formattedDate;
    if (startDate !== null) {
      formattedDate =
        typeof startDate === "string" && /^\d{4}_\d{2}_\d{2}$/.test(startDate)
          ? startDate
          : this.formatDateToStringForSlots(new Date(startDate));
    } else {
      const activeDayButton = document.querySelector(".SlotPicker-Day.isActive");
      formattedDate =
        event?.currentTarget?.dataset?.slotDate ||
        activeDayButton?.dataset?.slotDate ||
        this.formatDate(document.getElementById("picked_slot_day").value);
    }

    const csrfToken = document.querySelector('meta[name="csrf-token"]').content;
    const locale = this.getLocale();
    const schoolToken = document
      .getElementById("us-school-token")
      .innerHTML.trim();
    const response = await fetch(
      `/us/slots/${schoolToken}/${formattedDate}?is_automatic_transmission=${this.readAutomaticSelected()}&instructor_id=${this.readInstructorId()}&locale=${locale}&dashboard=true`,
      {
        method: "POST",
        headers: {
          Accept: "text/vnd.turbo-stream.html",
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({
          location_id: this.readLocationId(),
          guardian_pupil: this.readGuardianPupil(),
        }),
      }
    );

    if (response.ok) {
      const responseBody = await response.text();
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(responseBody, "text/html");
      const template = xmlDoc.querySelector("template");
      const targetElement = document.getElementById("potential_slots");

      if (template && targetElement) {
        targetElement.innerHTML = "";
        targetElement.appendChild(template.content.cloneNode(true));
      }

      if (targetElement) {
        const hasSlots = targetElement.querySelector(".SlotPicker-Time") !== null;
        this.displayDashboardWeekMessage(!hasSlots);
        this.toggleDashboardNoSlots(false);
        this.clearSelectedSlot();
      }
    }

    await this.hideLoader();
  }

  async slotsNextWeek(event) {
    event.preventDefault();
    if (event.currentTarget.disabled) {
      return;
    }

    const startingDate = this.getDisplayedWeekStartDate(event.currentTarget);
    let startDate = await this.getNextWeekStartDate(startingDate);
    startDate = this.formatDateToString(startDate);
    await this.get_next_lesson_slot_day(startDate, "calendar_next_week");
  }

  async slotsPreviousWeek(event) {
    event.preventDefault();
    if (event.currentTarget.disabled) {
      return;
    }

    const startingDate = this.getDisplayedWeekStartDate(event.currentTarget);
    let startDate = await this.getPreviousWeekStartDate(startingDate);
    startDate = this.formatDateToString(startDate);
    await this.get_next_lesson_slot_day(startDate, "calendar_previous_week");
  }

  async get_next_lesson_slot_day(startDate, action, options = {}) {
    await this.showLoader();

    const csrfToken = document.querySelector('meta[name="csrf-token"]').content;
    const locale = this.getLocale();
    const schoolToken = document
      .getElementById("us-school-token")
      .innerHTML.trim();
    const isInitialLoad = options.isInitialLoad === true;
    const response = await fetch(
      `/us/select-next-slot-day/${schoolToken}?start_time=${startDate}&week_action=${action}&week_change=false&is_automatic_transmission=${this.readAutomaticSelected()}&instructor_id=${this.readInstructorId()}&locale=${locale}&dashboard=true`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          Accept: "application/json",
        },
        body: JSON.stringify({
          location_id: this.readLocationId(),
          guardian_pupil: this.readGuardianPupil(),
          is_initial_load: isInitialLoad,
        }),
      }
    );

    if (!response.ok) {
      await this.hideLoader();
      return;
    }

    const data = await response.json();
    const startDateIso = data.selected_date;
    const selectedDay = data.selected_day;
    const weekStartDate = data.week_start_date;
    const noFutureSlots = data.no_future_slots === true;
    const earliestWeekStartDate = data.earliest_week_start_date;
    const latestWeekStartDate = data.latest_week_start_date;
    const timeZone = data.time_zone;

    this.element.dataset.lessonSlotPickerNoFutureSlots = noFutureSlots
      ? "true"
      : "false";
    if (earliestWeekStartDate) {
      this.element.dataset.lessonSlotPickerEarliestWeekStart =
        earliestWeekStartDate;
    }

    if (latestWeekStartDate) {
      this.element.dataset.lessonSlotPickerLatestWeekStart =
        latestWeekStartDate;
    }

    this.replaceDayPicker(data.day_picker_html);

    let resolvedStartDateIso = startDateIso;
    let resolvedSelectedDay = selectedDay;

    if (
      this.shouldUseInitialWeekFallback(weekStartDate, startDateIso, selectedDay)
    ) {
      resolvedStartDateIso =
        this.element.dataset.lessonSlotPickerInitialSelectedDate || null;
      const initialSelectedDay =
        this.element.dataset.lessonSlotPickerInitialSelectedDay;
      resolvedSelectedDay =
        initialSelectedDay === undefined || initialSelectedDay === ""
          ? null
          : parseInt(initialSelectedDay, 10);
    }

    this.setDisplayedWeekStart(weekStartDate, resolvedStartDateIso, timeZone);

    const hasSelectedDay =
      resolvedStartDateIso !== null && resolvedSelectedDay !== null;
    if (hasSelectedDay) {
      this.element.dataset.lessonSlotPickerCurrentDate = resolvedStartDateIso;
      const dateWithTimeZone = Luxon.DateTime.fromISO(resolvedStartDateIso, {
        zone: timeZone,
      }).startOf("day");
      await this.get_lesson_slots(null, dateWithTimeZone);
      this.select_slot_day(resolvedSelectedDay);
      this.disableScheduleButton();
      this.toggleDashboardNoSlots(false);
      this.displayDashboardWeekMessage(false);

      if (isInitialLoad) {
        this.element.dataset.lessonSlotPickerInitialWeekStart =
          this.element.dataset.lessonSlotPickerDisplayedWeekStart;
        this.element.dataset.lessonSlotPickerInitialSelectedDate =
          resolvedStartDateIso;
        this.element.dataset.lessonSlotPickerInitialSelectedDay = `${resolvedSelectedDay}`;
      }
    } else {
      this.clearDashboardSlots();
      const noSlotsAtAll = this.hasNoFutureSlots();
      this.toggleDashboardNoSlots(noSlotsAtAll);
      this.displayDashboardWeekMessage(!noSlotsAtAll);
      this.disableScheduleButton();
    }

    this.updateDashboardNavigationButtons();
    await this.hideLoader();
  }

  changeInstructor(event) {
    const nextInstructorId = event.detail?.id || "Any";
    if (`${this.currentInstructorId}` === `${nextInstructorId}`) {
      return;
    }

    this.currentInstructorId = nextInstructorId;
    this.refreshFromInitialWeek();
  }

  changeLocation(event) {
    const nextLocationId = event.detail?.id || "default";
    const previousZoneId = this.zoneIdForLocation(this.currentLocationId);
    const nextZoneId = this.zoneIdForLocation(nextLocationId);
    this.currentLocationId = nextLocationId;
    this.updateZoneFields(nextZoneId);

    if (`${previousZoneId}` === `${nextZoneId}`) {
      return;
    }

    const selectedInstructorId = this.readInstructorId();
    const instructorZoneMap = this.readInstructorZoneMap();
    const instructorZones = instructorZoneMap[`${selectedInstructorId}`] || [];

    if (
      selectedInstructorId &&
      !["Any", "earliest", ""].includes(`${selectedInstructorId}`) &&
      !instructorZones.includes(nextZoneId)
    ) {
      const instructorSelector = document.querySelector(
        '[data-controller~="driving-school-widgets-instructor-selector"]'
      );
      if (instructorSelector) {
        instructorSelector.dispatchEvent(
          new CustomEvent("resetInstructor", {
            detail: { id: "earliest" },
            bubbles: true,
          })
        );
      } else {
        this.refreshFromInitialWeek();
      }
      return;
    }

    this.refreshFromInitialWeek();
  }

  filter_on_transmission_change() {
    const nextTransmission = this.readAutomaticSelected();
    if (this.currentTransmission === nextTransmission) {
      return;
    }

    this.currentTransmission = nextTransmission;
    const displayedWeekStart =
      this.element.dataset.lessonSlotPickerDisplayedWeekStart ||
      this.element.dataset.lessonSlotPickerCurrentDate ||
      this.formatDateToString(new Date());
    this.clearSelectedSlot();
    this.get_next_lesson_slot_day(displayedWeekStart, "calendar_next_week");
  }

  refreshFromInitialWeek() {
    this.clearSelectedSlot();
    this.get_next_lesson_slot_day(this.formatDateToString(new Date()), "calendar_next_week", {
      isInitialLoad: true,
    });
  }

  setDisplayedWeekStart(weekStartDate, selectedDateIso, timeZone) {
    if (selectedDateIso) {
      this.element.dataset.lessonSlotPickerDisplayedWeekStart =
        Luxon.DateTime.fromISO(selectedDateIso, { zone: timeZone })
          .startOf("week")
          .toISODate();
      return;
    }

    if (weekStartDate) {
      this.element.dataset.lessonSlotPickerDisplayedWeekStart = weekStartDate;
    }
  }

  shouldUseInitialWeekFallback(weekStartDate, startDateIso, selectedDay) {
    if (startDateIso !== null || selectedDay !== null) {
      return false;
    }

    const initialWeekStart =
      this.element.dataset.lessonSlotPickerInitialWeekStart;
    const initialSelectedDate =
      this.element.dataset.lessonSlotPickerInitialSelectedDate;
    const initialSelectedDay =
      this.element.dataset.lessonSlotPickerInitialSelectedDay;

    if (
      !initialWeekStart ||
      !initialSelectedDate ||
      initialSelectedDay === ""
    ) {
      return false;
    }

    return weekStartDate === initialWeekStart;
  }

  displayDashboardWeekMessage(show) {
    const weekMessage = document.getElementById("no_slots_this_week");
    if (weekMessage) {
      weekMessage.hidden = !show;
    }
  }

  toggleDashboardNoSlots(show) {
    const noSlot = document.getElementById("no_slot_available");
    if (noSlot) {
      noSlot.hidden = !show;
    }

    if (show) {
      this.displayDashboardWeekMessage(false);
    }
  }

  updateDashboardNavigationButtons() {
    const previousButton = document.getElementById(
      "slots-picker-previous-week-button"
    );
    const nextButton = document.getElementById("slots-picker-next-week-button");
    if (!previousButton || !nextButton) {
      return;
    }

    const earliestWeekStartString =
      this.element.dataset.lessonSlotPickerEarliestWeekStart;
    const latestWeekStartString =
      this.element.dataset.lessonSlotPickerLatestWeekStart;
    const displayedWeekStartString =
      this.element.dataset.lessonSlotPickerDisplayedWeekStart;

    if (!displayedWeekStartString) {
      return;
    }

    if (this.hasNoFutureSlots()) {
      this.setArrowButtonState(previousButton, true);
      this.setArrowButtonState(nextButton, true);
      return;
    }

    const displayedWeekStart = Luxon.DateTime.fromISO(
      displayedWeekStartString
    ).startOf("week");

    let shouldDisablePrevious = false;
    if (earliestWeekStartString) {
      const earliestWeekStart = Luxon.DateTime.fromISO(
        earliestWeekStartString
      ).startOf("week");
      shouldDisablePrevious = displayedWeekStart <= earliestWeekStart;
    }

    let shouldDisableNext = false;
    if (latestWeekStartString) {
      const latestWeekStart = Luxon.DateTime.fromISO(
        latestWeekStartString
      ).startOf("week");
      shouldDisableNext = displayedWeekStart >= latestWeekStart;
    }

    this.setArrowButtonState(previousButton, shouldDisablePrevious);
    this.setArrowButtonState(nextButton, shouldDisableNext);
  }

  getDisplayedWeekStartDate(fallbackButton) {
    return (
      this.element.dataset.lessonSlotPickerDisplayedWeekStart ||
      fallbackButton.dataset.startingDate
    );
  }

  hasNoFutureSlots() {
    return this.element.dataset.lessonSlotPickerNoFutureSlots === "true";
  }

  clearDashboardSlots() {
    const targetElement = document.getElementById("potential_slots");
    if (targetElement) {
      targetElement.innerHTML = "";
    }
    this.clearSelectedSlot();
  }

  clearSelectedSlot() {
    this.timeButtonTargets.forEach((button) => {
      button.classList.remove("isActive");
    });

    this.resetPickedSlotFields();
  }

  select_slot_day(selectedDay) {
    this.dayButtonTargets.forEach((button) => {
      button.classList.remove("isActive");
    });

    const selectedDayButton = document.getElementById(`day-${selectedDay}`);
    if (!selectedDayButton) {
      return;
    }

    selectedDayButton.classList.add("isActive");
    selectedDayButton.classList.remove("Button--disabled");
    document.getElementById("picked_slot_day").value =
      selectedDayButton.textContent.trim();
  }

  resetPickedSlotFields() {
    document.getElementById("picked_slot_time").value = "";
    document.getElementById("picked_slot_iso_time").value = "";
    document.getElementById("picked_slot_id").value = "";
    document.getElementById("picked_slot_is_master_lesson").value = "false";
    document.getElementById("picked_slot_master_lesson_id").value = "";
    this.disableScheduleButton();
  }

  disableScheduleButton() {
    if (this.hasNextButtonSlotsTarget) {
      this.nextButtonSlotsTarget.disabled = true;
    }
  }

  enableScheduleButton() {
    if (this.hasNextButtonSlotsTarget) {
      this.nextButtonSlotsTarget.disabled = false;
    }
  }

  replaceDayPicker(html) {
    const targetElement = document.getElementById("slots-day-picker");
    if (targetElement) {
      targetElement.innerHTML = html;
    }
  }

  updateZoneFields(zoneId) {
    const pickUpZoneField = document.getElementById("slot_pick_up_zone_id");
    const dropOffZoneField = document.getElementById("slot_drop_off_zone_id");
    if (pickUpZoneField) {
      pickUpZoneField.value = zoneId || "";
    }
    if (dropOffZoneField) {
      dropOffZoneField.value = zoneId || "";
    }
  }

  zoneIdForLocation(locationId) {
    const map = this.readLocationZoneMap();
    const raw = map[`${locationId || "default"}`];
    return raw === undefined || raw === null ? null : Number(raw);
  }

  readLocationZoneMap() {
    return JSON.parse(this.element.dataset.lessonSlotPickerLocationZoneMap || "{}");
  }

  readInstructorZoneMap() {
    return JSON.parse(
      this.element.dataset.lessonSlotPickerInstructorZoneMap || "{}"
    );
  }

  readLocationId() {
    return document.getElementById("location_selector_id")?.value || "default";
  }

  readInstructorId() {
    return document.getElementById("instructor_selector_id")?.value || "Any";
  }

  readGuardianPupil() {
    return (
      document.querySelector('input[name="guardian_pupil"]')?.value || ""
    );
  }

  readAutomaticSelected() {
    const automaticPreferenceInput = document.getElementById(
      "driving_school_booking_gearbox_preference_automatic"
    );
    return automaticPreferenceInput ? automaticPreferenceInput.checked : true;
  }

  getLocale() {
    if (this.hasLocaleTarget) {
      return this.localeTarget.value;
    }

    return "en";
  }

  setArrowButtonState(button, disabled) {
    if (!button) {
      return;
    }

    button.disabled = disabled;
    if (disabled) {
      button.classList.add("Button--disabled");
    } else {
      button.classList.remove("Button--disabled");
    }
  }

  async getNextWeekStartDate(dateString) {
    const [year, month, day] = dateString.split("-");
    const nextWeekStartDate = new Date(year, month - 1, day);
    nextWeekStartDate.setDate(nextWeekStartDate.getDate() + 7);
    nextWeekStartDate.setHours(0, 0, 0, 0);
    return nextWeekStartDate;
  }

  async getPreviousWeekStartDate(dateString) {
    const [year, month, day] = dateString.split("-");
    const previousWeekStartDate = new Date(year, month - 1, day);
    previousWeekStartDate.setDate(previousWeekStartDate.getDate() - 7);
    previousWeekStartDate.setHours(0, 0, 0, 0);
    return previousWeekStartDate;
  }

  formatDate(inputStr) {
    const dateStr = inputStr.split(" ")[1];
    let [month, day] = dateStr.split("/");

    const currentDate = new Date();
    const currentMonth = currentDate.getMonth() + 1;
    const currentYear = currentDate.getFullYear();
    const currentDay = currentDate.getDate();

    const year =
      currentMonth > month || (currentMonth == month && currentDay > day)
        ? currentYear + 1
        : currentYear;

    return `${year}_${month}_${day}`;
  }

  formatDateToString(date) {
    return this.formatStringDate(date, "-");
  }

  formatDateToStringForSlots(date) {
    return this.formatStringDate(date, "_");
  }

  formatStringDate(date, separator) {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");

    return `${year}${separator}${month}${separator}${day}`;
  }
}
